/**
 * 인앱 고객 스냅샷 통지 — 페이지 로드 시점의 로그인 상태를 앱에 알린다(규약 3.8 setCustomer).
 * 앱은 이 값으로 푸시(Klaviyo) 프로필을 고객에 묶거나 익명으로 되돌린다.
 *
 * 규약 전문: docs/APP-BRIDGE.md §3.8
 *
 * 이 파일은 「언제 보낼지」만 안다. 페이로드 규약(타입·빈 값·필드 생략)은 app-bridge.js 가 안다.
 *
 * ── 세션 게이트 — 왜 매 로드마다 보내지 않는가 ──
 * 규약의 호출 시점은 「모든 페이지의 로드 완료 시」다. 앱이 직전 값과 비교해 바뀐 것만 Klaviyo 에
 * 반영하므로 매 로드 통지도 틀리지는 않지만, 지면을 넘길 때마다 브릿지를 두드릴 이유가 없다.
 * 그래서 **보낸 스냅샷의 지문을 sessionStorage 에 남기고, 같은 지문이면 건너뛴다.**
 *
 * 지문은 「보냈다」 플래그가 아니라 **스냅샷의 내용**에서 뽑는다. 플래그로 두면 세션 중에 로그인 ·
 * 로그아웃이 일어나도 첫 통지 이후로는 막혀서, 로그아웃한 기기로 이전 고객의 푸시가 계속 간다.
 * 내용이 바뀌면 지문도 달라지므로 그때는 다시 보낸다.
 *
 * ── 왜 localStorage 가 아닌가 ──
 * sessionStorage 는 앱을 다시 실행하면 비워진다. 그것이 필요하다 — 앱을 재설치하면 Klaviyo 프로필도
 * 새로 시작하는데, 지문이 살아 있으면 웹이 영원히 다시 통지하지 않는다. 재실행마다 한 번 보내는
 * 편이 안전하고, 값이 같으면 앱이 걸러낸다(규약 3.8).
 *
 * 앱의 탭은 각각 독립된 웹뷰라 sessionStorage 를 공유하지 않는다 — 탭마다 한 번씩 나간다.
 * 매 로드에서 매 탭이 보내던 것에 비하면 충분히 줄고, 정확성에는 영향이 없다.
 *
 * ── language 는 보내지 않는다 ──
 * setCustomer 는 언어를 다루지 않는다(2026-09-29 앱 유닛 요청). setCustomer 와 setLanguage 의 호출
 * 타이밍에 따라 앱 상태가 어긋날 수 있어 언어 통지는 setLanguage 로 일원화했다 — 로그인 직후의 계정
 * 언어 통지는 app-language.js 가 한다(규약 3.3). 여기에 다시 붙이지 않는다.
 *
 * ── 아직 붙지 않은 것 ──
 * · 「계정 이메일 변경 확정 직후」(규약 3.8 호출 시점)는 별도 배선이 없다. 이메일 변경은
 *   Shopify 계정 지면(브릿지가 없는 도메인)에서 일어나 돌아오는 길이 페이지 로드다.
 * · 동의 게이팅은 걸지 않았다. 규약은 로그인 여부와 무관하게 보내라고만 하고, Klaviyo 호출 여부는
 *   앱이 판단한다. 마케팅 동의에 묶어야 한다면 GBConsent.whenAllowed('marketing', …) 로 감싼다
 *   (docs/CONSENT.md).
 */
(function () {
  'use strict';

  var app = window.PiyonnaApp;
  var snapshot = window.PiyonnaAppCustomer;

  // 이메일을 전역에 남겨 두지 않는다 — 값은 이 클로저에만 둔다(docs/CONSENT.md 의 태도와 같다).
  // 인앱이 아닐 때도 지운다. 일반 브라우저에서도 window 에 남길 이유가 없다.
  try {
    delete window.PiyonnaAppCustomer;
  } catch (e) {
    window.PiyonnaAppCustomer = undefined;
  }

  // 일반 브라우저에서는 스토리지도 건드리지 않는다
  if (!app || !app.isAvailable()) return;

  var STORAGE_KEY = 'piyonna_app_customer';

  function text(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * 스냅샷 지문. 이메일을 스토리지에 남기지 않기 위한 값이다 — 암호학적 용도가 아니고,
   * 되돌릴 수 없으므로 동의 철회 시 지울 식별자도 아니다(docs/CONSENT.md).
   * @param {string} value
   * @returns {string}
   */
  function fingerprint(value) {
    var hash = 5381;

    for (var i = 0; i < value.length; i++) {
      hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
    }

    return (hash >>> 0).toString(36);
  }

  function readSent() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // 스토리지 차단(사파리 시크릿 등) → 게이트를 읽을 수 없다. 매 로드 통지로 되돌아간다
      return null;
    }
  }

  function writeSent(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // 기록하지 못하면 다음 로드에서 한 번 더 보낸다. 앱이 같은 값을 걸러내므로 해가 없다
    }
  }

  var customerId = text(snapshot && snapshot.customerId);
  var email = text(snapshot && snapshot.email);

  // 비로그인은 두 값이 모두 비어 지문이 상수가 된다 — 세션 안에서 reset 은 한 번만 나간다
  // 구분자 '|' 는 숫자 id 에도 이메일에도 들어갈 수 없다 — 두 값의 경계가 뭉개지지 않는다
  var signature = fingerprint(customerId + '|' + email);

  if (readSent() === signature) return;

  // 보낸 것만 기록한다. 채널이 없거나 호출이 실패했는데 기록하면 그 세션 내내 통지가 막힌다
  if (app.setCustomer({ customerId: customerId, email: email })) writeSent(signature);
})();
